import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, TwoUp, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

const COMBINATORS = [
 { name: 'Promise.all', when: 'All must succeed', settles: 'Rejects on the first failure (fail-fast)', tone: 'border-amber-400' },
 { name: 'Promise.allSettled', when: 'Want every outcome', settles: 'Never rejects — returns status for each', tone: 'border-green-400' },
 { name: 'Promise.race', when: 'First to settle wins', settles: 'Resolves/rejects with the first to finish', tone: 'border-blue-400' },
 { name: 'Promise.any', when: 'First success wins', settles: 'Resolves on first fulfilled; rejects only if all fail', tone: 'border-purple-400' },
];

export const JSPromisesAdvanced: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Promises Advanced</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    Coordinating many promises at once. How <code>Promise.all</code> overlaps waiting to cut latency, what fail-fast really means, and when running everything in parallel becomes a production hazard.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="17 min read" />
  </div>

  {/* 1. The Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>The home page of a food delivery app needs five independent pieces of data, each from a different backend service:</Para>
   <div className="flex flex-wrap gap-2 mb-4">{['User profile', 'Nearby restaurants', 'Active offers', 'Previous orders', 'Wallet balance'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = await getUser();\nconst restaurants = await getRestaurants();\nconst offers = await getOffers();\nconst orders = await getOrders();\nconst wallet = await getWallet();` }]} defaultLanguage="javascript" id="pa-seq" />
   <Para>It works — but if each API takes <Code>500ms</Code>, the total is <Code>500 × 5 = 2500ms</Code>. The user stares at a blank screen for 2.5 seconds.</Para>
   <Callout tone="warning" title="The key question">Do these APIs depend on each other? No — the wallet service doesn&apos;t need the offer service. So why are we waiting for one before starting the next?</Callout>
  </section>

  {/* 2. Why Existing Solutions Fail */}
  <section className="mb-12">
   <SectionHeading n={2}>Why Existing Solutions Fail</SectionHeading>
   <Para>We know how to run a single promise with <Code>await</Code> or <Code>.then()</Code>. But real systems run tens, hundreds, or thousands of promises, and different situations need different coordination:</Para>
   <TwoUp
    left={{ title: 'Sometimes you need…', items: ['Every operation to succeed', 'The fastest response to win'] }}
    right={{ title: 'Other times…', items: ['Some operations may fail', 'The first successful response wins'] }}
   />
   <Para>A single <Code>await</Code> can&apos;t express any of these. JavaScript provides specialized <Mark>promise combinators</Mark> for each scenario.</Para>
  </section>

  {/* 3. Definition */}
  <section className="mb-12">
   <SectionHeading n={3}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200"><Mark>Promise combinators</Mark> are async coordination tools that take a collection of promises and combine them into a single promise, each with different settling rules.</p>
   </div>
   <div className="my-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
    {COMBINATORS.map((c) => (
     <div key={c.name} className={`rounded-xl border-2 ${c.tone} bg-white dark:bg-gray-900 p-4`}>
      <div className="text-sm font-bold text-gray-900 dark:text-white font-mono mb-1">{c.name}()</div>
      <div className="text-[12px] text-gray-600 dark:text-gray-400 mb-1">Use when: {c.when}</div>
      <div className="text-[12px] text-gray-600 dark:text-gray-300">{c.settles}</div>
     </div>
    ))}
   </div>
  </section>

  {/* 4. Core Idea */}
  <section className="mb-12">
   <SectionHeading n={4}>The Core Idea</SectionHeading>
   <Para>Instead of babysitting each promise individually, hand the whole group to JavaScript and let it coordinate. For independent data, that means starting everything at once:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const [user, orders, offers] = await Promise.all([\n  getUser(),\n  getOrders(),\n  getOffers(),\n]);\n// result is [user, orders, offers], in input order` }]} defaultLanguage="javascript" id="pa-all-core" />
   <Para><Code>Promise.all</Code> is one of the most heavily used promise APIs in production.</Para>
  </section>

  {/* 5. Internal Working */}
  <section className="mb-12">
   <SectionHeading n={5}>Internal Working</SectionHeading>
   <Callout tone="warning" title="Myth: Promise.all creates threads">
    A very common interview trap. <Code>Promise.all</Code> does <Mark>not</Mark> create multiple threads. JavaScript stays single-threaded.
   </Callout>
   <Para>It simply <Mark>starts</Mark> all the async operations without waiting for the previous one to finish. The actual waiting is done by the browser/Node runtime and the OS — not by JavaScript. Step by step:</Para>
   <ol className="my-4 space-y-1.5 text-[14px] text-gray-700 dark:text-gray-300 list-decimal list-inside">
    <li>All functions execute immediately — <Code>getUser()</Code>, <Code>getOrders()</Code>, <Code>getOffers()</Code>.</li>
    <li>Each kicks off its work (network request, DB query, file read).</li>
    <li>JavaScript keeps running; it does not sit idle.</li>
    <li>Results arrive independently, in any order.</li>
    <li>Once all promises have fulfilled, <Code>Promise.all</Code> resolves with an ordered array.</li>
   </ol>
  </section>

  {/* 6. Visual Flow */}
  <section className="mb-12">
   <SectionHeading n={6}>Visual Flow</SectionHeading>
   <CodeBlock snippets={[{ language: 'text', code: `User API   ↘\nOrders API  →  Promise.all  →  all complete  →  [user, orders, offers]\nOffers API ↗` }]} defaultLanguage="text" id="pa-visual" />
   <Para>All three start together; <Code>Promise.all</Code> resolves only after the last one settles — and preserves input order in the result regardless of completion order.</Para>
  </section>

  {/* 7. Simple Example: faster how */}
  <section className="mb-12">
   <SectionHeading n={7}>Simple Example: Why It&apos;s Faster</SectionHeading>
   <TwoUp
    left={{ title: 'Sequential await', items: ['User → Orders → Offers', '500 + 500 + 500', 'Total ≈ 1500ms'] }}
    right={{ title: 'Promise.all', items: ['User, Orders, Offers together', 'overlapping waits', 'Total ≈ 500ms'] }}
   />
   <Callout tone="info" title="What actually improved">
    <Code>Promise.all</Code> did <Mark>not</Mark> make any individual API faster — each still takes 500ms. The gain comes from <Mark>overlapping the waiting time</Mark>.
   </Callout>
  </section>

  {/* 8. Deep Example: throughput not computation */}
  <section className="mb-12">
   <SectionHeading n={8}>Deep Example: Throughput, Not Computation</SectionHeading>
   <Para>Because the speedup comes from overlapping <em>waiting</em>, <Code>Promise.all</Code> helps <Mark>I/O-bound</Mark> work, not CPU-bound work:</Para>
   <TwoUp
    left={{ title: 'I/O-bound (helped)', items: ['Network requests', 'Database queries', 'File reads', 'Mostly spent waiting'] }}
    right={{ title: 'CPU-bound (not helped)', items: ['Image processing', 'Big computations', 'Mostly spent computing', 'Needs Workers, not Promise.all'] }}
   />
   <Para>Five seconds of CPU work doesn&apos;t drop to one second just because it&apos;s inside <Code>Promise.all</Code> — the single thread still has to do the computing.</Para>
  </section>

  {/* 9. Production Example */}
  <section className="mb-12">
   <SectionHeading n={9}>Production Example</SectionHeading>
   <Para>The food-delivery dashboard, loaded in roughly one round-trip instead of five:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const [user, restaurants, offers, wallet] = await Promise.all([\n  getUser(),\n  getRestaurants(),\n  getOffers(),\n  getWallet(),\n]);` }]} defaultLanguage="javascript" id="pa-prod" />
   <div className="flex flex-wrap gap-2">{['Lower latency', 'Better UX', 'Faster page load'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
  </section>

  {/* 10. Engineering Tradeoffs: fail-fast + allSettled */}
  <section className="mb-12">
   <SectionHeading n={10}>Engineering Tradeoffs</SectionHeading>
   <SubHeading>Fail-fast behaviour</SubHeading>
   <Para>If any promise rejects, <Code>Promise.all</Code> rejects <Mark>immediately</Mark> — the other results are discarded, even if they would have succeeded:</Para>
   <CodeBlock snippets={[{ language: 'text', code: `Success + Failure + Success  →  Promise.all REJECTS` }]} defaultLanguage="text" id="pa-failfast" />
   <Para>This is right when you need <em>all</em> the data (user + permissions + subscription) — fail-fast stops incomplete state from spreading. It&apos;s wrong when failures are independent:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `// Email, SMS, push are independent — one failing shouldn't kill the rest\nconst results = await Promise.allSettled([\n  sendEmail(),\n  sendSMS(),\n  sendPush(),\n]);\n// [{status:'fulfilled',value}, {status:'rejected',reason}, ...]` }]} defaultLanguage="javascript" id="pa-allsettled" />
   <Para><Code>Promise.allSettled</Code> never rejects — it reports the outcome of every promise so you can handle partial success.</Para>
   <SubHeading>race and any</SubHeading>
   <Para><Code>Promise.race</Code> settles with the first promise to finish (useful for timeouts); <Code>Promise.any</Code> resolves with the first to <em>succeed</em>, ignoring earlier failures.</Para>
  </section>

  {/* 11. Performance Considerations: the 10k request problem */}
  <section className="mb-12">
   <SectionHeading n={11}>Performance Considerations</SectionHeading>
   <Para>&quot;Faster&quot; tempts developers to use <Code>Promise.all</Code> everywhere. Consider firing one request per user across 100,000 users:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `await Promise.all(users.map(sendEmail)); // users.length === 100_000` }]} defaultLanguage="javascript" id="pa-10k" />
   <div className="flex flex-wrap gap-2 mb-4">{['100k simultaneous requests', 'Memory explosion', 'Connection exhaustion', 'Provider rate limits'].map((e) => <Pill key={e}>❌ {e}</Pill>)}</div>
   <Para>This can crash the app. The fix is <Mark>bounded concurrency</Mark> — process in batches or use a worker pool so only N run at a time:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `async function inBatches(items, size, task) {\n  for (let i = 0; i < items.length; i += size) {\n    const batch = items.slice(i, i + size);\n    await Promise.all(batch.map(task)); // only 'size' run at once\n  }\n}\n\nawait inBatches(users, 50, sendEmail);` }]} defaultLanguage="javascript" id="pa-batches" />
   <Callout tone="info" title="Junior vs senior mindset">
    Junior: &quot;How much parallelism <em>can</em> we create?&quot; Senior: &quot;How much parallelism can we <Mark>safely sustain</Mark>?&quot; — the difference that leads to concurrency limits, batching, and worker pools.
   </Callout>
  </section>

  {/* 12. Common Bugs */}
  <section className="mb-12">
   <SectionHeading n={12}>Common Bugs</SectionHeading>
   <Callout tone="warning" title="Sequential when it could be parallel">Awaiting independent calls one by one instead of with <Code>Promise.all</Code>.</Callout>
   <Callout tone="warning" title="Using all when failures are independent">Letting one non-critical failure reject the whole batch — use <Code>allSettled</Code>.</Callout>
   <Callout tone="warning" title="Unbounded parallelism">Mapping a huge array straight into <Code>Promise.all</Code> and exhausting memory or connections.</Callout>
   <Callout tone="warning" title="Expecting partial results from all">Assuming successful promises return when one rejects — fail-fast discards them.</Callout>
  </section>

  {/* 13. Debugging Stories */}
  <section className="mb-12">
   <SectionHeading n={13}>Debugging Stories</SectionHeading>
   <Callout tone="info" title="The notification that killed the batch">
    A nightly job sent email, SMS, and push via <Code>Promise.all</Code>. One flaky SMS gateway rejected and the whole job aborted — emails that had already succeeded were reported as failures. Switching to <Code>Promise.allSettled</Code> let the successes stand and isolated the SMS failure.
   </Callout>
   <Callout tone="info" title="The server that fell over at scale">
    A migration script did <Code>Promise.all(users.map(...))</Code> over 100k users. It worked in a 10-user test, then exhausted DB connections in production. Batching to 50 at a time made it stable — same total work, sustainable concurrency.
   </Callout>
  </section>

  {/* 14. Interview Discussion */}
  <section className="mb-12">
   <SectionHeading n={14}>Interview Discussion</SectionHeading>
   <div className="flex flex-wrap gap-2 mb-5">
    {['Does Promise.all use threads?', 'Why is Promise.all faster?', 'all vs allSettled?', 'race vs any?', 'What is fail-fast?', 'When NOT to use Promise.all?', 'How to limit concurrency?', 'I/O-bound vs CPU-bound?'].map((q) => <Pill key={q}>{q}</Pill>)}
   </div>
   <QA q="Does Promise.all run promises on multiple threads?">
    No. JavaScript stays single-threaded. <Code>Promise.all</Code> just starts all the operations without waiting between them; the runtime and OS handle the concurrent I/O. The speedup is from overlapping waiting, not parallel computation.
   </QA>
   <QA q="What is the difference between Promise.all and Promise.allSettled?">
    <Code>all</Code> is fail-fast — it rejects on the first failure and discards other results. <Code>allSettled</Code> never rejects; it waits for every promise and returns each one&apos;s status (<Code>fulfilled</Code>/<Code>rejected</Code>), ideal for independent operations.
   </QA>
   <QA q="When should you NOT use Promise.all?">
    When operations are independent and partial success is acceptable (use <Code>allSettled</Code>), or when the collection is huge and unbounded parallelism would exhaust memory, connections, or hit rate limits (use batching / a concurrency limit).
   </QA>
   <QA q="What is the difference between race and any?">
    <Code>race</Code> settles with the first promise to settle — whether it fulfills or rejects. <Code>any</Code> resolves with the first to <em>fulfill</em> and only rejects if every promise fails.
   </QA>
  </section>

  {/* 15. Mental Model */}
  <section className="mb-12">
   <SectionHeading n={15}>Mental Model</SectionHeading>
   <Para>Think of placing five food orders at once instead of one-at-a-time. All kitchens cook in parallel, so you wait roughly for the slowest dish, not the sum of all of them. <Code>Promise.all</Code> = &quot;all dishes must arrive&quot;; <Code>allSettled</Code> = &quot;tell me how each dish turned out&quot;; <Code>race</Code> = &quot;serve whatever&apos;s ready first&quot;; <Code>any</Code> = &quot;serve the first dish that didn&apos;t burn.&quot;</Para>
  </section>

  {/* 16. Interview One-Liner */}
  <section className="mb-12">
   <SectionHeading n={16}>Interview One-Liner</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4">
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     Promise combinators coordinate multiple promises — <code>Promise.all</code> overlaps independent I/O to cut latency (fail-fast), while <code>allSettled</code>, <code>race</code>, and <code>any</code> handle partial success and first-to-finish scenarios.
    </p>
   </div>
  </section>

  {/* 17. Most Important Takeaway */}
  <section className="mb-4">
   <SectionHeading n={17}>Most Important Takeaway</SectionHeading>
   <Callout tone="info" title="Takeaway">
    <Code>Promise.all</Code> speeds up independent I/O by overlapping waiting — not by adding threads or speeding up computation. It&apos;s fail-fast, so use <Code>allSettled</Code> when failures are independent. And at scale, unbounded parallelism is a hazard: bound concurrency with batching or worker pools. Choose the combinator that matches the workload, not just the fastest-sounding one.
   </Callout>
  </section>
 </div>
);

export default JSPromisesAdvanced;
